/**
 * Console navigation: one source of truth for the sidebar, page titles and the route table.
 * Groups follow ADMIN_DESIGN.md. `cap` names the capability a staff member needs to see an item; the API enforces
 * the same capability on its routes regardless of what the UI shows.
 */
import type { ConsoleCapability } from "@flexfreight/core";

export type ConsoleNavItem = { to: string; label: string; cap: ConsoleCapability; end?: boolean };
export type ConsoleNavGroup = { id: string; label: string; items: ConsoleNavItem[] };

export const consoleNav: ConsoleNavGroup[] = [
  { id: "overview", label: "Overview", items: [{ to: "/", label: "Dashboard", cap: "dashboard:read", end: true }] },
  {
    id: "sales",
    label: "Sales",
    items: [
      { to: "/quotes/new", label: "Quoting center", cap: "quotes:create" },
      { to: "/quotes", label: "Quotes", cap: "quotes:read", end: true },
    ],
  },
  {
    id: "customers",
    label: "Customers",
    items: [
      { to: "/customers", label: "All customers", cap: "customers:read" },
      { to: "/signups", label: "Signups & approvals", cap: "customers:read" },
      { to: "/leads", label: "Leads", cap: "leads:read" },
    ],
  },
  {
    id: "freight",
    label: "Freight",
    items: [
      { to: "/shipments", label: "Shipments", cap: "shipments:read" },
      { to: "/dispatch", label: "Dispatch board", cap: "shipments:read" },
      { to: "/claims", label: "Claims", cap: "claims:read" },
      { to: "/credit", label: "Credit", cap: "credit:read" },
      { to: "/billing", label: "Billing reconciliation", cap: "billing:read" },
      { to: "/amfast", label: "AmFast tenders", cap: "shipments:read" },
      { to: "/import", label: "Shipment import", cap: "shipments:import" },
      { to: "/carrier-health", label: "Carrier health", cap: "carriers:read" },
    ],
  },
  {
    id: "service",
    label: "Service",
    items: [
      { to: "/tickets", label: "Tickets", cap: "tickets:read" },
      { to: "/knowledge", label: "Knowledge base", cap: "kb:read" },
    ],
  },
  {
    id: "email",
    label: "Email",
    items: [
      { to: "/inbox", label: "Inbox", cap: "email:read" },
      { to: "/inbox/templates", label: "Templates", cap: "email:read" },
    ],
  },
  {
    id: "marketing",
    label: "Marketing",
    items: [
      { to: "/marketing/campaigns", label: "Campaigns", cap: "marketing:read" },
      { to: "/marketing/audiences", label: "Audiences", cap: "marketing:read" },
      { to: "/marketing/sequences", label: "Sequences", cap: "marketing:read" },
      { to: "/marketing/social", label: "Social", cap: "marketing:read" },
      { to: "/marketing/seo", label: "SEO & site analytics", cap: "marketing:read" },
      { to: "/marketing/brand", label: "Brand & media", cap: "marketing:read" },
    ],
  },
  {
    id: "insights",
    label: "Insights",
    items: [
      { to: "/reports", label: "Reports", cap: "reports:read" },
      { to: "/ai", label: "Flex AI", cap: "ai:use" },
    ],
  },
  {
    id: "admin",
    label: "Admin Center",
    items: [
      { to: "/admin", label: "All settings", cap: "settings:read", end: true },
      { to: "/admin/carriers", label: "Carriers & API keys", cap: "carriers:manage" },
      { to: "/admin/integrations", label: "Integrations", cap: "integrations:manage" },
      { to: "/admin/team", label: "Team & permissions", cap: "staff:manage" },
      { to: "/admin/company", label: "Company & policies", cap: "settings:read" },
      { to: "/admin/audit", label: "Audit log", cap: "audit:read" },
      { to: "/admin/problems", label: "Problem reports", cap: "settings:read" },
      { to: "/admin/health", label: "System health", cap: "settings:read" },
    ],
  },
];

/** Groups and items the given capabilities allow (UI convenience only; the API enforces access). */
export function navFor(caps: readonly string[]): ConsoleNavGroup[] {
  const have = new Set(caps);
  return consoleNav
    .map((g) => ({ ...g, items: g.items.filter((i) => have.has(i.cap)) }))
    .filter((g) => g.items.length > 0);
}

export const allNavItems: ConsoleNavItem[] = consoleNav.flatMap((g) => g.items);

/** Longest segment-aligned prefix match, so /customers/abc resolves to "All customers". */
export function navItemFor(pathname: string): ConsoleNavItem | undefined {
  const path = pathname.replace(/\/+$/, "") || "/";
  let best: ConsoleNavItem | undefined;
  for (const item of allNavItems) {
    const hit = item.end || item.to === "/" ? path === item.to : path === item.to || path.startsWith(`${item.to}/`);
    if (hit && (!best || item.to.length > best.to.length)) best = item;
  }
  return best;
}

export function consoleTitle(pathname: string): string {
  const item = navItemFor(pathname);
  return item ? `${item.label} | FlexFreight Admin` : "FlexFreight Admin";
}

/** The group holding the current route (undefined off the nav, e.g. the not-found page). */
export function navGroupFor(pathname: string): ConsoleNavGroup | undefined {
  const item = navItemFor(pathname);
  return item ? consoleNav.find((g) => g.items.includes(item)) : undefined;
}

/* ------------------------------------------------------------------------------------------------------------------
 * Legacy per-user collapsed-group helpers. The shell no longer uses them: the sidebar is now an accordion (every
 * group collapsed by default, one open at a time, the current route's group opens on navigation; see ConsoleShell).
 * ---------------------------------------------------------------------------------------------------------------- */
export const collapsedKeyFor = (userId: string) => `ffa-nav-collapsed:${userId}`;

/** Group ids the user collapsed; [] when nothing is stored or storage is unavailable. */
export function readCollapsedGroups(userId: string): string[] {
  try {
    const raw = localStorage.getItem(collapsedKeyFor(userId));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    const known = new Set(consoleNav.map((g) => g.id));
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string" && known.has(v)) : [];
  } catch {
    return [];
  }
}

export function writeCollapsedGroups(userId: string, ids: readonly string[]): void {
  try {
    if (ids.length === 0) localStorage.removeItem(collapsedKeyFor(userId));
    else localStorage.setItem(collapsedKeyFor(userId), JSON.stringify(ids));
  } catch {
    /* storage unavailable (private window, blocked): the choice just isn't remembered */
  }
}

/** Collapsed ids after one group is toggled. */
export function toggleCollapsed(collapsed: readonly string[], id: string): string[] {
  return collapsed.includes(id) ? collapsed.filter((g) => g !== id) : [...collapsed, id];
}

/** Collapsed ids with the current route's group forced open. */
export function openActiveGroup(collapsed: readonly string[], pathname: string): string[] {
  const active = navGroupFor(pathname);
  return active && collapsed.includes(active.id) ? collapsed.filter((g) => g !== active.id) : [...collapsed];
}
